import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthState } from '../app/store/authSlice.js'
import { validateApiKey } from '../activity-stream/apiKeyUtils.js'

const MAX_BUFFER_SIZE = 1000
const RECONNECT_DELAY_MS = 500       // Start reconnecting after 500ms
const MAX_RECONNECT_DELAY_MS = 5_000 // Cap at 5 seconds

const useActivityStream = ({ autoStart = true, onStoredKeyUsed } = {}) => {
  const { apiKey, writeKey, isAuthenticated } = useSelector(selectAuthState)

  const [connection, setConnection] = useState('disconnected')
  const [status, setStatus] = useState({ message: '', tone: 'info' })
  const [events, setEvents] = useState([])
  const [metrics, setMetrics] = useState({ total: 0, lastEventAt: null })
  const [topicAvailable, setTopicAvailable] = useState(false)
  const [pausedEventIds, setPausedEventIds] = useState(new Set())
  const [isManuallyPaused, setIsManuallyPaused] = useState(false)

  const controllerRef = useRef(null)
  const decoderRef = useRef(null)
  const mountedRef = useRef(true)
  const filterParamsRef = useRef('')
  const reconnectAttemptRef = useRef(0)
  const shouldAutoReconnectRef = useRef(true)

  if (!decoderRef.current) {
    decoderRef.current = new TextDecoder('utf-8')
  }

  const updateStatus = useCallback((message, tone = 'info') => {
    setStatus({ message, tone })
  }, [])

  const appendEvent = useCallback((payload, eventType) => {
    const eventId = payload.id ?? `${eventType}-${Date.now()}-${Math.random()}`
    const timestamp = payload.timestamp ?? Date.now()

    setEvents((prev) => {
      const next = [
        {
          id: eventId,
          type: eventType,
          timestamp,
          raw: payload,
          ...payload,
        },
        ...prev,
      ]
      if (next.length > MAX_BUFFER_SIZE) {
        next.length = MAX_BUFFER_SIZE
      }
      return next
    })

    setMetrics((prev) => ({
      total: prev.total + 1,
      lastEventAt: Date.now(),
    }))
  }, [])

  const stopStream = useCallback((reason = 'disconnect') => {
    if (controllerRef.current) {
      controllerRef.current.abortReason = reason
      controllerRef.current.abort()
      controllerRef.current = null
    }
  }, [])

  const resetState = useCallback(() => {
    setEvents([])
    setMetrics({ total: 0, lastEventAt: null })
    setTopicAvailable(false)
    setPausedEventIds(new Set())
    setIsManuallyPaused(false)
  }, [])

  const connect = useCallback(async (filterParams = '') => {
    const trimmedApiKey = apiKey?.trim() ?? ''
    if (!trimmedApiKey) {
      updateStatus('No API key configured', 'warning')
      return
    }

    const validation = validateApiKey(trimmedApiKey)
    if (!validation.valid) {
      updateStatus(validation.message, 'error')
      return
    }

    onStoredKeyUsed?.()

    stopStream('reconnect')
    resetState()
    setConnection('connecting')
    updateStatus('')
    shouldAutoReconnectRef.current = true

    // Store filter params for reconnection
    filterParamsRef.current = filterParams

    const controller = new AbortController()
    controllerRef.current = controller
    const decoder = decoderRef.current

    const baseUrl =
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_ACTIVITY_API_BASE_URL) ||
      '/api/activity'

    const url = filterParams ? `${baseUrl}/stream?${filterParams}` : `${baseUrl}/stream`

    try {
      const headers = {
        'x-api-key': trimmedApiKey,
      }
      if (writeKey) {
        headers.writekey = writeKey
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })

      if (response.status === 401 || response.status === 403) {
        // An expired or refused key does not get better by asking again: stop. One tab left
        // open with a key that expired on 14 Sep 2026 re-asked every 5 s for two weeks.
        shouldAutoReconnectRef.current = false
        setConnection('error')
        updateStatus('The API key has expired or is not allowed. Sign in again to resume.', 'error')
        return
      }
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      let bufferText = ''

      while (true) {
        const { value, done } = await reader.read()
        if (done) {
          break
        }
        bufferText += decoder.decode(value, { stream: true })
        const segments = bufferText.split('\n\n')
        bufferText = segments.pop() ?? ''

        segments.forEach((segment) => {
          if (!segment.trim()) {
            return
          }
          let eventName = 'message'
          const dataLines = []
          segment.split('\n').forEach((line) => {
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim())
            }
          })

          if (!dataLines.length) {
            return
          }

          const payloadText = dataLines.join('\n')

          if (eventName === 'heartbeat' || eventName === 'ping') {
            return
          }

          try {
            const payload = JSON.parse(payloadText)
            if (isManuallyPaused) {
              setPausedEventIds((prev) => new Set(prev).add(payload.id ?? `${eventName}-${Date.now()}`))
            } else {
              appendEvent(payload, eventName)
            }
            if (eventName !== 'heartbeat' && eventName !== 'ping') {
              setConnection('connected')
              reconnectAttemptRef.current = 0  // Reset on successful connection
            }
            if (eventName === 'semantic-event') {
              setTopicAvailable(true)
            }
          } catch (err) {
            console.error(`[ActivityStream] Unable to parse ${eventName} payload`, err)
          }
        })
      }

      if (!controller.abortReason && shouldAutoReconnectRef.current && mountedRef.current) {
        // Stream closed unexpectedly - schedule reconnection
        reconnectAttemptRef.current += 1
        const delay = Math.min(
          RECONNECT_DELAY_MS * 2 ** Math.max(0, reconnectAttemptRef.current - 1),
          MAX_RECONNECT_DELAY_MS
        )
        setConnection('reconnecting')
        updateStatus(`Reconnecting in ${Math.ceil(delay / 1000)}s...`, 'warning')
        setTimeout(() => {
          if (shouldAutoReconnectRef.current && mountedRef.current) {
            connect(filterParamsRef.current)
          }
        }, delay)
      } else if (!controller.abortReason) {
        updateStatus('Stream closed by server', 'warning')
        setConnection('disconnected')
      }
    } catch (error) {
      const abortReason = controller.abortReason
      if (abortReason) {
        if (abortReason === 'pause') {
          setConnection('paused')
          updateStatus('Stream paused', 'info')
        } else if (abortReason === 'disconnect') {
          setConnection('disconnected')
          updateStatus('Disconnected', 'info')
          shouldAutoReconnectRef.current = false
        } else if (abortReason === 'reconnect') {
          // handled by the next connection
        } else {
          setConnection('disconnected')
          updateStatus('Stream stopped', 'warning')
        }
      } else if (shouldAutoReconnectRef.current && mountedRef.current) {
        // Error occurred - schedule reconnection
        console.error('[ActivityStream] Connection error:', error)
        reconnectAttemptRef.current += 1
        const delay = Math.min(
          RECONNECT_DELAY_MS * 2 ** Math.max(0, reconnectAttemptRef.current - 1),
          MAX_RECONNECT_DELAY_MS
        )
        setConnection('reconnecting')
        updateStatus(`Reconnecting in ${Math.ceil(delay / 1000)}s...`, 'warning')
        setTimeout(() => {
          if (shouldAutoReconnectRef.current && mountedRef.current) {
            connect(filterParamsRef.current)
          }
        }, delay)
      } else {
        console.error(error)
        setConnection('error')
        updateStatus('Failed to connect to stream', 'error')
      }
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null
      }
    }
  }, [apiKey, writeKey, isManuallyPaused, appendEvent, onStoredKeyUsed, resetState, stopStream, updateStatus])

  // Disconnect is no longer exposed - streams stay connected when authenticated
  // To go offline, user must select "Offline" in the API key selector

  const pause = useCallback(() => {
    if (connection !== 'connected') {
      return
    }
    setConnection('paused')
    setIsManuallyPaused(true)
    updateStatus('Stream paused - events buffering in background', 'info')
  }, [connection, updateStatus])

  const resume = useCallback(() => {
    if (connection !== 'paused') {
      return
    }
    setConnection('connected')
    setIsManuallyPaused(false)
    setPausedEventIds(new Set())
    updateStatus('Stream resumed', 'success')
  }, [connection, updateStatus])

  const clearEvents = useCallback(() => {
    setEvents([])
    setMetrics({ total: 0, lastEventAt: null })
  }, [])

  // Auto-connect on mount if authenticated
  useEffect(() => {
    if (autoStart && isAuthenticated && apiKey) {
      connect(filterParamsRef.current)
    }

    return () => {
      mountedRef.current = false
      stopStream('unmount')
    }
  }, [autoStart, isAuthenticated, apiKey, connect, stopStream])

  return {
    connection,
    status,
    events,
    metrics,
    topicAvailable,
    pausedEventIds,
    connect,
    pause,
    resume,
    clearEvents,
    isAuthenticated,
  }
}

export default useActivityStream
